import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import type { KanbanStage } from '@/components/kanban/KanbanBoard'

// ============================================================
// TYPES
// ============================================================

export interface ProjectCard {
  id: string
  name: string
  number: number
  typeName: string
  color: string
  priority: number
  budget: number | null
  deadline: string | null
  responsibleName: string
  stageId: string
  taskCount: number
  bookingCount: number
  activeTaskCount: number
  doneTaskCount: number
  totalCost: number
  doneCost: number
  progressPercent: number
  createdAt: string
}

interface ProjectFilters {
  search: string
  typeId: string
  responsibleId: string
}

interface ProjectsKanbanState {
  stages: KanbanStage[]
  projects: Record<string, ProjectCard[]>
  loading: boolean
  filters: ProjectFilters
  snapshot: Record<string, ProjectCard[]> | null
}

const initialFilters: ProjectFilters = {
  search: '',
  typeId: '',
  responsibleId: ''
}

const initialState: ProjectsKanbanState = {
  stages: [],
  projects: {},
  loading: false,
  filters: initialFilters,
  snapshot: null
}

// ============================================================
// HELPERS
// ============================================================

function buildQueryParams(filters: ProjectFilters): URLSearchParams {
  const params = new URLSearchParams()

  // Fetch all projects for Kanban (high limit)
  params.set('limit', '200')
  params.set('page', '1')

  // quick-260806-6q9: служебные проекты (Project.isSystem) на доску не выводятся — они
  // существуют ради группировки денег в фин-отчёте, работы по ним не ведут. Опт-аут стоит
  // здесь, а не в сервисе по умолчанию: тот же роут кормит подбор проекта в форме транзакции
  // Финансов, где служебный проект обязан оставаться выбираемым.
  params.set('excludeSystem', 'true')

  if (filters.search) params.set('search', filters.search)
  if (filters.typeId) params.set('typeId', filters.typeId)
  if (filters.responsibleId) params.set('responsibleId', filters.responsibleId)

  return params
}

function groupProjectsByStage(
  projects: ProjectCard[],
  stages: KanbanStage[]
): Record<string, ProjectCard[]> {
  const grouped: Record<string, ProjectCard[]> = {}

  for (const stage of stages) {
    grouped[stage.id] = []
  }

  for (const project of projects) {
    if (grouped[project.stageId]) {
      grouped[project.stageId].push(project)
    }
  }

  return grouped
}

function mapApiProjectToCard(project: Record<string, unknown>): ProjectCard {
  const type = project.type as { name: string } | null
  const responsible = project.responsible as { id: string; name: string } | null
  const counts = project._count as { tasks: number; bookings: number } | null

  return {
    id: project.id as string,
    name: project.name as string,
    number: project.number as number,
    typeName: type?.name ?? '',
    color: (project.color as string | null) ?? '#6366F1',
    priority: project.priority as number,
    budget: project.budget as number | null,
    deadline: project.deadline as string | null,
    responsibleName: responsible?.name ?? '',
    stageId: project.stageId as string,
    taskCount: counts?.tasks ?? 0,
    bookingCount: counts?.bookings ?? 0,
    activeTaskCount: (project.activeTaskCount as number | undefined) ?? 0,
    doneTaskCount: (project.doneTaskCount as number | undefined) ?? 0,
    totalCost: (project.totalCost as number | undefined) ?? 0,
    doneCost: (project.doneCost as number | undefined) ?? 0,
    progressPercent: (project.progressPercent as number | undefined) ?? 0,
    createdAt: project.createdAt as string
  }
}

// ============================================================
// THUNKS
// ============================================================

export const fetchProjectStages = createAsyncThunk(
  'projectsKanban/fetchStages',
  async () => {
    const res = await fetch('/api/project-stages')
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to fetch project stages')
    }

    return json.data as KanbanStage[]
  }
)

export const fetchProjects = createAsyncThunk(
  'projectsKanban/fetchProjects',
  async (_, { getState }) => {
    const state = getState() as { projectsKanbanReducer: ProjectsKanbanState }
    const { filters } = state.projectsKanbanReducer
    const params = buildQueryParams(filters)

    const res = await fetch(`/api/projects?${params}`)
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to fetch projects')
    }

    return (json.data as Record<string, unknown>[]).map(mapApiProjectToCard)
  }
)

export const moveProjectStage = createAsyncThunk(
  'projectsKanban/moveStage',
  async ({ projectId, stageId }: { projectId: string; stageId: string }) => {
    const res = await fetch(`/api/projects/${projectId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stageId })
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to move project')
    }

    return { projectId, stageId }
  }
)

export const createProject = createAsyncThunk(
  'projectsKanban/createProject',
  async (data: { name: string; typeId: string; [key: string]: unknown }) => {
    const res = await fetch('/api/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to create project')
    }

    return mapApiProjectToCard(json.data as Record<string, unknown>)
  }
)

// ============================================================
// SLICE
// ============================================================

export const projectsKanbanSlice = createSlice({
  name: 'projectsKanban',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ProjectFilters>>) => {
      return {
        ...state,
        filters: { ...state.filters, ...action.payload }
      }
    },
    clearFilters: (state) => {
      return {
        ...state,
        filters: initialFilters
      }
    }
  },
  extraReducers: (builder) => {
    // fetchProjectStages
    builder.addCase(fetchProjectStages.fulfilled, (state, action) => {
      const stages = action.payload.map((s) => ({
        id: s.id,
        name: s.name,
        color: s.color,
        sortOrder: s.sortOrder
      }))

      return { ...state, stages }
    })

    // fetchProjects
    builder.addCase(fetchProjects.pending, (state) => {
      return { ...state, loading: true }
    })

    builder.addCase(fetchProjects.fulfilled, (state, action) => {
      const projects = groupProjectsByStage(action.payload, state.stages)

      return { ...state, projects, loading: false }
    })

    builder.addCase(fetchProjects.rejected, (state) => {
      return { ...state, loading: false }
    })

    // moveProjectStage -- optimistic update with snapshot for rollback
    builder.addCase(moveProjectStage.pending, (state, action) => {
      const { projectId, stageId: toStageId } = action.meta.arg

      // Save snapshot for rollback
      const snapshot: Record<string, ProjectCard[]> = {}

      for (const [key, cards] of Object.entries(state.projects)) {
        snapshot[key] = [...cards]
      }

      // Find and move project optimistically
      let movedProject: ProjectCard | null = null
      const updatedProjects: Record<string, ProjectCard[]> = {}

      for (const [sId, cards] of Object.entries(state.projects)) {
        const filtered = cards.filter(p => {
          if (p.id === projectId) {
            movedProject = { ...p, stageId: toStageId }

            return false
          }

          return true
        })

        updatedProjects[sId] = filtered
      }

      if (movedProject && updatedProjects[toStageId]) {
        updatedProjects[toStageId] = [...updatedProjects[toStageId], movedProject]
      }

      return { ...state, projects: updatedProjects, snapshot }
    })

    builder.addCase(moveProjectStage.fulfilled, (state) => {
      // Optimistic update already applied; clear snapshot
      return { ...state, snapshot: null }
    })

    builder.addCase(moveProjectStage.rejected, (state) => {
      // Rollback to snapshot
      if (state.snapshot) {
        return { ...state, projects: state.snapshot, snapshot: null }
      }

      return state
    })

    // createProject
    builder.addCase(createProject.fulfilled, (state, action) => {
      const project = action.payload
      const stageProjects = state.projects[project.stageId] ?? []
      const updatedProjects = {
        ...state.projects,
        [project.stageId]: [...stageProjects, project]
      }

      return { ...state, projects: updatedProjects }
    })
  }
})

export const { setFilters, clearFilters } = projectsKanbanSlice.actions

export default projectsKanbanSlice.reducer
